const ASCII_LOGO = `
 ██████╗ ██████╗ ███████╗███████╗██████╗ ██╗   ██╗
 ██╔══██╗██╔══██╗██╔════╝██╔════╝██╔══██╗╚██╗ ██╔╝
 ██║  ██║██████╔╝█████╗  ███████╗██████╔╝ ╚████╔╝
 ██║  ██║██╔══██╗██╔══╝  ╚════██║██╔══██╗  ╚██╔╝
 ██████╔╝██║  ██║███████╗███████║██████╔╝   ██║
 ╚═════╝ ╚═╝  ╚═╝╚══════╝╚══════╝╚═════╝    ╚═╝
`;

interface Command {
  run: (args: string[]) => string | Promise<string>;
  help: string;
}

type NavigateFn = (page: string) => void;

export class DresbyTerminal {
  private output: HTMLElement;
  private input: HTMLInputElement;
  private overlay: HTMLElement;
  private history: string[] = [];
  private historyIndex = -1;
  private navigate: NavigateFn;

  private commands: Record<string, Command> = {};

  constructor(navigate: NavigateFn) {
    this.navigate = navigate;
    this.output  = document.getElementById('terminal-output')!;
    this.input   = document.getElementById('terminal-input') as HTMLInputElement;
    this.overlay = document.getElementById('terminal-overlay')!;

    this.registerCommands();
    this.bindEvents();
  }

  private registerCommands(): void {
    this.commands = {
      help: {
        help: 'List available commands',
        run: () => [
          '<span class="terminal-line accent">DRESBY TERMINAL v1.0</span>',
          '',
          'Commands:',
          '  <span class="glow-red">help</span>          — show this message',
          '  <span class="glow-red">about</span>         — about Dresby',
          '  <span class="glow-red">apps</span>          — navigate to Apps',
          '  <span class="glow-red">github</span>        — navigate to GitHub Projects',
          '  <span class="glow-red">store</span>         — navigate to Store',
          '  <span class="glow-red">blog</span>          — navigate to Devlog',
          '  <span class="glow-red">contact</span>       — navigate to Contact',
          '  <span class="glow-red">home</span>          — return to hub',
          '  <span class="glow-red">ls</span>            — list all apps',
          '  <span class="glow-red">whoami</span>        — identify yourself',
          '  <span class="glow-red">version</span>       — Dresby software versions',
          '  <span class="glow-red">clear</span>         — clear terminal',
          '  <span class="glow-red">easteregg</span>     — 🥚',
          '',
        ].join('\n'),
      },

      about: {
        help: 'About Dresby',
        run: () => {
          this.navigate('about');
          return '<span class="success">Navigating to About...</span>';
        },
      },

      apps: {
        help: 'View all apps',
        run: () => {
          this.navigate('apps');
          return '<span class="success">Loading apps...</span>';
        },
      },

      github: {
        help: 'View GitHub projects',
        run: () => {
          this.navigate('projects');
          return '<span class="success">Opening GitHub explorer...</span>';
        },
      },

      store: {
        help: 'Visit store',
        run: () => {
          this.navigate('store');
          return '<span class="success">Opening store...</span>';
        },
      },

      blog: {
        help: 'View devlog',
        run: () => {
          this.navigate('blog');
          return '<span class="success">Loading devlog...</span>';
        },
      },

      contact: {
        help: 'Contact page',
        run: () => {
          this.navigate('contact');
          return '<span class="success">Opening contact page...</span>';
        },
      },

      home: {
        help: 'Return to hub',
        run: () => {
          this.navigate('home');
          return '<span class="success">Returning to hub...</span>';
        },
      },

      ls: {
        help: 'List apps',
        run: async () => {
          const { default: apps } = await import('../data/apps.json');
          const lines = ['<span class="accent">DRESBY APPS:</span>', ''];
          (apps as any[]).forEach(app => {
            lines.push(`  ${app.icon} <span class="glow-red">${app.name.padEnd(18)}</span> <span style="color:var(--text-3)">${app.tagline}</span>`);
          });
          lines.push('');
          lines.push(`  ${apps.length} total. Type <span class="glow-red">apps</span> to view gallery.`);
          return lines.join('\n');
        },
      },

      whoami: {
        help: 'Identify',
        run: () => [
          '',
          `  uid=1000(visitor) gid=1000(visitor)`,
          `  groups=1000(visitor),dresby(werkstatt-zugang)`,
          `  shell=/usr/bin/dresby-terminal`,
          `  location=dresby.de/hub`,
          '',
        ].join('\n'),
      },

      version: {
        help: 'Software versions',
        run: async () => {
          const { default: apps } = await import('../data/apps.json');
          const lines = ['<span class="accent">INSTALLED VERSIONS:</span>', ''];
          (apps as any[]).filter((a: any) => a.version).forEach((app: any) => {
            lines.push(`  ${app.name.padEnd(20)} v${app.version}`);
          });
          return lines.join('\n');
        },
      },

      clear: {
        help: 'Clear output',
        run: () => { this.output.innerHTML = ''; return ''; },
      },

      easteregg: {
        help: '?',
        run: () => [
          '',
          '<span class="accent">🌊 DRESBY SECRET LOG:</span>',
          '',
          '  Coordinates: 51.0504° N, 13.7373° E',
          '  Die Elbe fließt durch die Stadt.',
          '  Kaffee: Dresdner Röstung.',
          '  Current project: classified.',
          '  Status: shipping.',
          '',
          '  "Technology should feel like magic,',
          '   not like paperwork."',
          '',
          '  — Team Dresby, Santa Cruz CA',
          '',
        ].join('\n'),
      },
    };
  }

  private bindEvents(): void {
    document.getElementById('terminal-btn')?.addEventListener('click', () => this.open());
    document.getElementById('terminal-close')?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === '`' && e.ctrlKey) {
        e.preventDefault();
        this.toggle();
      }
      if (e.key === 'Escape' && !this.overlay.classList.contains('hidden')) {
        this.close();
      }
    });

    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const cmd = this.input.value.trim();
        this.input.value = '';
        if (cmd) {
          this.history.unshift(cmd);
          this.historyIndex = -1;
          this.exec(cmd);
        }
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.historyIndex = Math.min(this.historyIndex + 1, this.history.length - 1);
        this.input.value = this.history[this.historyIndex] ?? '';
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.historyIndex = Math.max(this.historyIndex - 1, -1);
        this.input.value = this.historyIndex >= 0 ? this.history[this.historyIndex] : '';
      }

      if (e.key === 'Tab') {
        e.preventDefault();
        const partial = this.input.value.trim();
        const matches = Object.keys(this.commands).filter(c => c.startsWith(partial));
        if (matches.length === 1) this.input.value = matches[0];
        else if (matches.length > 1) this.writeLine(`<span class="info">${matches.join('  ')}</span>`);
      }
    });
  }

  private async exec(rawCmd: string): Promise<void> {
    const [cmd, ...args] = rawCmd.split(' ');
    this.writeLine(`<span class="cmd">dresby@werkstatt:~$ ${rawCmd}</span>`);

    const handler = this.commands[cmd];
    if (!handler) {
      this.writeLine(`<span class="error">Command not found: ${cmd}. Try 'help'.</span>`);
      return;
    }

    try {
      const result = await handler.run(args);
      if (result) this.writeLine(result);
    } catch (err) {
      this.writeLine(`<span class="error">Error: ${err}</span>`);
    }
    this.scrollBottom();
  }

  private writeLine(html: string): void {
    const lines = html.split('\n');
    lines.forEach(line => {
      const el = document.createElement('div');
      el.className = 'terminal-line';
      el.innerHTML = line;
      this.output.appendChild(el);
    });
    this.scrollBottom();
  }

  private scrollBottom(): void {
    this.output.scrollTop = this.output.scrollHeight;
  }

  open(): void {
    this.overlay.classList.remove('hidden');
    this.input.focus();

    if (this.output.children.length === 0) {
      this.writeLine(`<pre class="terminal-line accent" style="font-size:0.55rem;line-height:1.2">${ASCII_LOGO}</pre>`);
      this.writeLine('<span class="info">Dresby Terminal v1.0 — Type <span class="glow-red">help</span> for commands. Ctrl+` to toggle.</span>');
      this.writeLine('');
    }
  }

  close(): void {
    this.overlay.classList.add('hidden');
  }

  toggle(): void {
    if (this.overlay.classList.contains('hidden')) this.open();
    else this.close();
  }
}
